/**
 * TokenRefreshQueue
 *
 * Manages token refresh operations to ensure only one refresh is in-flight at a time
 * Multiple concurrent refresh requests share the same promise
 * Queues API requests during token refresh and retries them after refresh completes
 *
 * Based on research.md: Promise-based queue pattern for token refresh
 */

import type { QueuedRequest } from "@/types/queued-request";

export class TokenRefreshQueue {
  private refreshPromise: Promise<void> | null = null;
  private refreshFn: () => Promise<void>;
  private pendingRequests: QueuedRequest[] = [];

  /**
   * Create a new TokenRefreshQueue
   *
   * @param refreshFn - Function to call for token refresh (should update tokens in storage)
   */
  constructor(refreshFn: () => Promise<void>) {
    this.refreshFn = refreshFn;
  }

  /**
   * Request a token refresh
   * If a refresh is already in-flight, returns the existing promise
   * Otherwise, starts a new refresh operation
   *
   * @returns Promise that resolves when refresh completes
   */
  async refresh(): Promise<void> {
    // If refresh already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start new refresh
    this.refreshPromise = (async () => {
      try {
        await this.refreshFn();
        // Refresh succeeded - process pending requests
        await this.processPendingRequests();
      } catch (error) {
        // Refresh failed - reject pending requests
        await this.processPendingRequests(error as Error);
        throw error; // Re-throw to maintain error propagation
      } finally {
        // Clear the promise when refresh completes (success or failure)
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Queue a request to be retried after token refresh completes
   * Used when a request receives 401 during an in-flight refresh
   *
   * @param request - QueuedRequest with resolve/reject callbacks and retry function
   */
  queueRequest<T = unknown>(request: QueuedRequest<T>): void {
    this.pendingRequests.push(request as QueuedRequest);
  }

  /**
   * Process all pending requests after token refresh completes
   * If refresh succeeded: retry all requests with new token
   * If refresh failed: reject all requests with refresh error
   *
   * @param refreshError - Error from token refresh (if failed)
   */
  async processPendingRequests(refreshError?: Error): Promise<void> {
    const requests = this.pendingRequests.splice(0); // Clear queue

    if (refreshError) {
      // Refresh failed - reject all queued requests
      requests.forEach((request) => {
        request.reject(refreshError);
      });
      return;
    }

    // Refresh succeeded - retry all queued requests
    await Promise.all(
      requests.map(async (request) => {
        try {
          const result = await request.retry();
          request.resolve(result);
        } catch (error) {
          request.reject(error);
        }
      }),
    );
  }

  /**
   * Check if a refresh is currently in progress
   *
   * @returns true if refresh is in-flight, false otherwise
   */
  isRefreshing(): boolean {
    return this.refreshPromise !== null;
  }
}
